<app-start-screen *ngIf="view === 'start'"
  [livePreview]="livePreview"
  [liveAnimated]="liveAnimated"
  [designs]="designs"
  [sessionsEnabled]="sessionsEnabled"
  [sessionLive]="sessionLive"
  [sessionCountdown]="sessionCountdown"
  [sessionPeople]="sessionPeopleCount"
  (draw)="startDraw()"
  (photo)="startPhoto()"
  (gallery)="openGallery()"
  (session)="startSession()"
  (join)="joinSession()">
</app-start-screen>

<div class="led-studio session-studio" *ngIf="view === 'session'">
  <div class="studio-bar">
    <button type="button" class="icon-btn back" (click)="goStart()" aria-label="Leave the session" title="Leave">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6"></path></svg>
    </button>
    <h1 class="studio-title">Drawing together</h1>
    <span class="live-pill"><span class="dot" aria-hidden="true"></span>LIVE</span>
  </div>

  <div class="people-row">
    <span class="dots">
      <span class="person me" [style.background]="myColor" title="You"></span>
      <span class="person" *ngFor="let c of otherCursors" [style.background]="c.color"></span>
    </span>
    <span class="people-count">{{ sessionPeopleCount }} drawing</span>
  </div>

  <div class="stage">
    <app-led-grid-editor
      [frame]="frame"
      (frameChange)="onSessionFrameChange($event)"
      [color]="color"
      [tool]="tool"
      [cursors]="otherCursors"
      [selectMode]="false"
      (colorPicked)="onColorPicked($event)">
    </app-led-grid-editor>
  </div>

  <app-tool-dock
    [tool]="tool"
    [color]="color"
    [recentColors]="recentColors"
    [brightness]="brightness"
    [sessionMode]="true"
    [countdown]="sessionCountdown"
    [peopleCount]="sessionPeopleCount"
    (toolChange)="setTool($event)"
    (pick)="pickColor($event)"
    (brightnessChange)="brightness = $event"
    (leaveSession)="goStart()"
    [multiFrame]="false"
    [hasImage]="false"
    (importPhoto)="triggerImport()"
    (nudge)="onNudge($event.dx, $event.dy)">
  </app-tool-dock>
</div>

<div class="led-studio" *ngIf="view === 'draw'">
  <input #fileInput type="file" accept="image/*" hidden (change)="onImportFile($event)" />
  <div class="studio-bar">
    <button type="button" class="icon-btn back" (click)="goStart()" aria-label="Back to the start" title="Back">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6"></path></svg>
    </button>
    <h1 class="studio-title">Draw</h1>
    <div class="zoom-group" role="group" aria-label="Zoom">
      <button type="button" class="zoom-btn" (click)="zoomOut()" [disabled]="!zoomed" aria-label="Zoom out" title="Zoom out">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"></path></svg>
      </button>
      <button type="button" class="zoom-btn" (click)="zoomIn()" [disabled]="!canZoomIn" aria-label="Zoom in" title="Zoom in">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"></path><path d="M12 5v14"></path></svg>
      </button>
    </div>
    <button type="button" class="icon-btn fit-btn" *ngIf="zoomed" (click)="fitBoard()"
            aria-label="Fit the whole board" title="Fit the whole board">
      <span class="zoom-label">{{ zoomLabel }}</span>
    </button>

    <div class="history" role="group" aria-label="History">
      <button type="button" class="icon-btn" (click)="undo()" [disabled]="!canUndo" aria-label="Undo" title="Undo">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 14 4 9l5-5"></path><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11"></path></svg>
      </button>
      <button type="button" class="icon-btn" (click)="redo()" [disabled]="!canRedo" aria-label="Redo" title="Redo">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 14 5-5-5-5"></path><path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13"></path></svg>
      </button>
    </div>
  </div>

  <div class="stage">
    <app-led-grid-editor
      [frame]="frame"
      (frameChange)="onFrameChange($event)"
      [color]="color"
      [tool]="tool"
      [selectMode]="selectMode"
      (strokeStart)="onStrokeStart()"
      (colorPicked)="onColorPicked($event)">
    </app-led-grid-editor>
    <app-animation-timeline *ngIf="showTimeline"
      [(frames)]="frames"
      [(activeIndex)]="activeIndex"
      (hide)="stopAnimating()">
    </app-animation-timeline>

    <button type="button" class="start-anim" *ngIf="!showTimeline" (click)="startAnimating()">
      <span class="ic" aria-hidden="true">🎬</span>
      <span class="txt">
        <span class="t">Start an animation</span>
        <span class="s">Copies this picture into a second frame</span>
      </span>
    </button>
  </div>

  <div class="save-row">
    <label class="sr-only" for="design-name">Design name</label>
    <input id="design-name" type="text" placeholder="Name it to save for later" [(ngModel)]="saveName" />
    <button type="button" class="save-btn" [disabled]="!saveName.trim()" (click)="save()">💾 Save</button>
  </div>

  <app-tool-dock
    [tool]="tool"
    [color]="color"
    [recentColors]="recentColors"
    [brightness]="brightness"
    [sending]="sending"
    [playing]="playing"
    [loop]="loop"
    (toolChange)="setTool($event)"
    (pick)="pickColor($event)"
    (clear)="clearBoard()"
    (brightnessChange)="brightness = $event"
    (send)="sendActive()"
    (togglePreview)="togglePreview()"
    (loopChange)="setLoop($event)"
    [fitMode]="fitMode"
    [multiFrame]="frames.length > 1"
    [hasImage]="hasImage"
    (importPhoto)="triggerImport()"
    (fitModeChange)="setFitMode($event)"
    [imgBrightness]="imgBrightness"
    [imgContrast]="imgContrast"
    [imgSaturation]="imgSaturation"
    [cropX]="cropX"
    [cropY]="cropY"
    (imgBrightnessChange)="setImgBrightness($event)"
    (imgContrastChange)="setImgContrast($event)"
    (imgSaturationChange)="setImgSaturation($event)"
    (cropXChange)="setCropX($event)"
    (cropYChange)="setCropY($event)"
    (resetImport)="resetImport()"
    [pasteEnabled]="!!copiedFrame"
    (copyFrame)="copyFrame()"
    (pasteFrame)="pasteFrame()"
    (nudge)="onNudge($event.dx, $event.dy)"
    [selectMode]="selectMode"
    (toggleSelect)="toggleSelect()"
    (deselect)="deselect()">
  </app-tool-dock>
</div>

<div class="queue-view" *ngIf="view === 'queue'">
  <div class="studio-bar">
    <button type="button" class="icon-btn back" (click)="backToDraw()" aria-label="Back to drawing" title="Back">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6"></path></svg>
    </button>
    <h1 class="studio-title">The board</h1>
  </div>

  <app-queue-status
    [myKey]="myQueueKey"
    [queue]="queue"
    [now]="nowShowing"
    [clock]="clock"
    [thumbnail]="myThumbnail"
    [canSave]="myIsEditorWork"
    [canManage]="isOwner"
    (remove)="removeFromQueue($event)"
    (clearAll)="clearQueue()"
    (leave)="leaveLine()"
    (again)="goStart()"
    (save)="saveFromQueue($event)">
  </app-queue-status>
</div>

<div class="library" *ngIf="view === 'gallery'">
  <div class="studio-bar">
    <button type="button" class="icon-btn back" (click)="goStart()" aria-label="Back to the start" title="Back">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6"></path></svg>
    </button>
    <h1 class="studio-title">Pick a design</h1>
  </div>

  <app-design-gallery
    [designs]="designs"
    [sending]="sending"
    (load)="editDesign($event)"
    (send)="sendDesign($event)">
  </app-design-gallery>
</div>
